import type { BusinessRules } from "@teamna/shared";
import { useState, type ReactNode } from "react";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { formatMoney } from "@teamna/web-client/lib/format.ts";
import { ChipRow, Field, FormRow, FormSection } from "../components/index.ts";
import { parseAmountFils } from "../owner/parent/panels.tsx";

/** The same limits as the API (`feeFilsSchema`): AED 2, the smallest card payment, to AED 10,000. */
export const MIN_FEE_FILS = 200;
export const MAX_FEE_FILS = 1_000_000;

type FeeKey = "noShowFeeFils" | "lateCancelFeeFils";

/** The start value of a fee that the owner turns on. */
const START_FEE_FILS = 5_000;

/** The error for a typed amount, or null when it is a fee the API takes. */
export function feeAmountError(text: string): string | null {
  const fils = parseAmountFils(text);
  if (fils === null) return "Write an amount in AED, for example 50 or 50.50.";
  if (fils < MIN_FEE_FILS) return "The smallest fee is AED 2.";
  if (fils > MAX_FEE_FILS) return "The largest fee is AED 10,000.";
  return null;
}

function amountText(fils: number): string {
  return fils % 100 === 0 ? String(fils / 100) : (fils / 100).toFixed(2);
}

/**
 * No-show and late-cancel fee on Packages and rules (Pilates studio shape I2). For each one the
 * business takes the session only (Off, the start value for every business), or takes the session
 * and asks for a fee. Teamna sends nothing by itself: each fee waits on the To do list of Home,
 * where the owner sends it with a card link or waives it. Business level: every class uses it.
 *
 * `onInvalid` tells the page that a typed amount is not a fee yet, so it does not save.
 */
export function FeeRulesSection({
  rules,
  onChange,
  onInvalid,
  footer,
}: {
  rules: BusinessRules;
  onChange: (next: BusinessRules) => void;
  onInvalid?: (invalid: boolean) => void;
  /** The page's own save button: the fees save with the cancellation rule. */
  footer?: ReactNode;
}) {
  const { label } = useLabels();
  const [typed, setTyped] = useState<Partial<Record<FeeKey, string>>>({});

  function set(key: FeeKey, fils: number | null) {
    onChange({ ...rules, [key]: fils });
  }

  function type(key: FeeKey, text: string) {
    const next = { ...typed, [key]: text };
    setTyped(next);
    const error = feeAmountError(text);
    onInvalid?.(Object.values(next).some((value) => value !== undefined && feeAmountError(value) !== null));
    if (!error) set(key, parseAmountFils(text));
  }

  function row(key: FeeKey, title: string, when: string) {
    const fils = rules[key] ?? null;
    const on = fils !== null;
    const text = typed[key] ?? (fils !== null ? amountText(fils) : "");
    const error = typed[key] !== undefined ? feeAmountError(typed[key]!) : null;
    return (
      <FormRow label={title}>
        <ChipRow
          label={title}
          wrap
          options={[
            { id: "off", label: "Take the session only" },
            { id: "on", label: "Take the session and ask for a fee" },
          ]}
          selectedId={on ? "on" : "off"}
          onSelect={(id) => {
            const next = { ...typed };
            delete next[key];
            setTyped(next);
            onInvalid?.(Object.values(next).some((value) => value !== undefined && feeAmountError(value) !== null));
            set(key, id === "on" ? (fils ?? START_FEE_FILS) : null);
          }}
        />
        {on ? (
          <div className="mt-3 max-w-[240px]">
            <Field
              label="Fee in AED"
              value={text}
              onChange={(event) => type(key, event.target.value)}
              inputMode="decimal"
              error={error ?? undefined}
              hint={error ? undefined : `${formatMoney(fils)} ${when}`}
            />
          </div>
        ) : null}
      </FormRow>
    );
  }

  return (
    <FormSection
      title="Fees"
      description={`Teamna sends nothing by itself. Each fee waits on your To do list, where you send it with a card link or waive it. Every ${label("group")} uses these fees.`}
    >
      {row("noShowFeeFils", "No-show", `when a booked ${label("participant")} is marked absent.`)}
      {row("lateCancelFeeFils", "Late cancel", `when a ${label("member")} cancels after the free cancellation time.`)}
      {footer ? <div className="py-4">{footer}</div> : null}
    </FormSection>
  );
}
